import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase/client.js';
import type { Project, ProjectStatus } from '../../types/project.js';
import type { Visibility } from '../../types/visibility.js';

const COL = 'projects';

export interface ProjectInput {
  name: string;
  description: string;
  color: string;
  status: ProjectStatus;
  visibility: Visibility;
}

function toDate(v: unknown): Date {
  if (v && typeof v === 'object' && 'toDate' in v) {
    const fn = (v as { toDate: () => Date }).toDate;
    if (typeof fn === 'function') return fn.call(v);
  }
  return new Date();
}

function fromDoc(id: string, data: Record<string, unknown>): Project {
  // 互換: 旧 seed の `owner` フィールドを `createdBy` の代替として読む
  const createdBy = String(data['createdBy'] ?? data['owner'] ?? '');
  return {
    id,
    name: String(data['name'] ?? ''),
    description: String(data['description'] ?? ''),
    color: String(data['color'] ?? '#888'),
    status: (data['status'] ?? '試作') as ProjectStatus,
    visibility: (data['visibility'] ?? 'private') as Visibility,
    createdBy,
    createdAt: toDate(data['createdAt']),
    updatedAt: toDate(data['updatedAt']),
    ...(typeof data['links'] === 'number' ? { links: data['links'] as number } : {}),
    ...(typeof data['questions'] === 'number'
      ? { questions: data['questions'] as number }
      : {}),
    ...(typeof data['notes'] === 'number' ? { notes: data['notes'] as number } : {}),
    ...(typeof data['apps'] === 'number' ? { apps: data['apps'] as number } : {}),
  };
}

export async function findShared(opts?: { count?: number }): Promise<Project[]> {
  const q = query(
    collection(db, COL),
    where('visibility', '==', 'shared'),
    orderBy('createdAt', 'desc'),
    limit(opts?.count ?? 100),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => fromDoc(d.id, d.data()));
}

export async function findByOwner(uid: string, opts?: { count?: number }): Promise<Project[]> {
  const q = query(
    collection(db, COL),
    where('createdBy', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(opts?.count ?? 100),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => fromDoc(d.id, d.data()));
}

/** admin / 内部用途。SPA からの一覧表示は findShared を使う */
export async function findAll(): Promise<Project[]> {
  const snap = await getDocs(query(collection(db, COL)));
  return snap.docs.map((d) => fromDoc(d.id, d.data()));
}

export async function findById(id: string): Promise<Project | null> {
  const snap = await getDoc(doc(db, `${COL}/${id}`));
  if (!snap.exists()) return null;
  return fromDoc(snap.id, snap.data());
}

export async function create(uid: string, input: ProjectInput): Promise<string> {
  const data: DocumentData = {
    ...input,
    createdBy: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, COL), data);
  return ref.id;
}

export async function update(id: string, patch: Partial<ProjectInput>): Promise<void> {
  await updateDoc(doc(db, `${COL}/${id}`), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function remove(id: string): Promise<void> {
  await deleteDoc(doc(db, `${COL}/${id}`));
}
